import { useDispatch, useSelector } from 'react-redux';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  activeDateSelector,
  activePageSelector,
  availableDatesSelector,
  createActiveFundSelectorBySection,
  createFundNameFromFundIdSelector,
  createPageDetailsSelector,
  createSectionByIdSelector,
  currentFundSearchStringSelector,
} from '../../../redux/pages/selectors';
import { Section } from '../../../types/server-data/ClientConfig';
import React, { FunctionComponent, useEffect } from 'react';
import { clearPdfExports } from '../../../redux/fileExport/actions';
import {
  updatePage,
  updateSection,
  updateFund,
} from '../../../redux/pages/actions';
import { Grid, Typography } from '@mui/material';
import { clientNameSelector } from '../../../redux/auth/selectors';
import NoDataMessage from '../../feedback/NoDataMessage.component';
import clsx from 'clsx';
import useTitleBarSelect from '../../../hooks/useTitleBarSelect';
import GridItem from '../GridComponents/GridItem';
import OverviewCard from '../../cards/OverviewCard';
import { mainColors, RaptorTheme } from '../../../styling/theme';
import useRouteParams from '../../../hooks/useRouteParams';
import { makeStyles } from '@mui/styles';
import { useAppendOverviewFunds } from '../../../hooks/useAppendOverviewFunds';
import GeneralComponentErrorShield from '../../general/GeneralComponentErrorShield';
import { sortFundNames } from '../../../utilities/customSorting';
import RaptorLoading from '../../feedback/RaptorLoading';

export const createResourceCacheKey = (
  sectionResources?: string[],
  pageResources?: string[],
) => {
  if (pageResources && pageResources.length > 0) {
    return pageResources;
  }
  if (sectionResources && sectionResources.length > 0) {
    return sectionResources;
  }
  return [];
};

// Props for overview pages (no fund selection required)
export interface GeneralOverviewPageRenderedComponentProps {
  section: string;
  positionDate?: string;
  availableDates?: string[];
  pageId: string;
}

// Props for fund-specific pages (requires fund selection)
export interface FundInfoComponentProps
  extends GeneralOverviewPageRenderedComponentProps {
  fundId: string;
  fundName: string;
}

interface SimpleGeneralPageWrapperProps {
  children?: React.ReactNode;
  pageId: string;
  requireFundSelection?: boolean;
  Component?: React.ComponentType<any>;
  /** Fill the layout pane and clip overflow (single-card pages like overview). */
  fillAvailableSpace?: boolean;
}

const Wrapper: React.FC<{
  children: React.ReactNode;
  fundsData: any;
  fillAvailableSpace?: boolean;
}> = ({ children, fundsData, fillAvailableSpace }) => {
  return (
    <GeneralComponentErrorShield
      dataObjects={[fundsData]}
      customLoadingMessages={[`Loading funds data...`]}
    >
      <Grid
        container
        spacing={fillAvailableSpace ? 0 : 2}
        sx={
          fillAvailableSpace
            ? {
                boxSizing: 'border-box',
                height: '100%',
                m: 0,
                maxWidth: '100%',
                minHeight: 0,
                minWidth: 0,
                overflow: 'hidden',
                p: 1,
                width: '100%',
              }
            : { p: '8px' }
        }
      >
        {children}
      </Grid>
    </GeneralComponentErrorShield>
  );
};

export const SimpleGeneralPageWrapper: React.FC<
  SimpleGeneralPageWrapperProps
> = ({
  pageId,
  children,
  Component,
  requireFundSelection,
  fillAvailableSpace,
}) => {
  const { section } = useParams<any>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const activePage = useSelector(activePageSelector);
  const activeDate = useSelector(activeDateSelector);

  const pageDetails = useSelector(createPageDetailsSelector(pageId!, section!));

  //   Is this required to be here as activeFundSelector exists?
  const { fundId } = useRouteParams(['fundId']);
  const fundName = useSelector(createFundNameFromFundIdSelector(fundId || ''));
  const availableDates = useSelector(availableDatesSelector);

  // Get section details at the top level
  const sectionDetailsSelector = createSectionByIdSelector(section!);
  const sectionDetails: Section | null = useSelector(sectionDetailsSelector);
  const activeFundSelector = createActiveFundSelectorBySection(
    section!,
    pageId,
  );

  const activeFund = useSelector(activeFundSelector);

  const rsResources = createResourceCacheKey(
    sectionDetails?.rsResources,
    pageDetails?.rsResources,
  );

  const fundsData = useAppendOverviewFunds(
    sectionDetails?.fundTypes,
    rsResources,
  ) ?? {
    isFetching: true,
    error: null,
    data: [],
  };

  useEffect(() => {
    dispatch(clearPdfExports());
  }, [fundId]);

  useEffect(() => {
    dispatch(updatePage(pageId!, pageDetails?.pageArea || '', section!));
    dispatch(clearPdfExports());
  }, [pageId]);

  useEffect(() => {
    dispatch(updateSection(section!));
    dispatch(clearPdfExports());
  }, [section, dispatch]);

  // Update active fund in Redux when fundId changes
  useEffect(() => {
    if (!fundId) {
      dispatch(updateFund(section!, '', activeDate));
    } else {
      dispatch(updateFund(section!, fundId, activeDate));
    }
  }, [dispatch, section, pageId, fundId, activeDate, activeFund?.id]);

  useEffect(() => {
    if (
      pageDetails &&
      activeFund &&
      pageDetails.allowFundPages &&
      location.search.indexOf('fundId') === -1
    ) {
      // Use URLSearchParams to preserve existing params (like positionDate)
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('fundId', activeFund.id);
      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    }
  }, [activeFund, pageDetails, location.pathname, location.search, navigate]);

  const isWaitingForPositionDate =
    !!pageDetails?.usePositionDatePicker &&
    !pageDetails?.usePositionDatePickerRange &&
    !activeDate;

  const positionDateLoading = (
    <RaptorLoading centerWrap messages={['Loading position date...']} />
  );

  if (!fundId) {
    if (Component) {
      return (
        <Wrapper fundsData={fundsData} fillAvailableSpace={fillAvailableSpace}>
          {isWaitingForPositionDate ? (
            positionDateLoading
          ) : (
            <Component positionDate={activeDate} section={section!} />
          )}
        </Wrapper>
      );
    }

    if (requireFundSelection) {
      return (
        <Wrapper fundsData={fundsData} fillAvailableSpace={fillAvailableSpace}>
          <SimpleSelectAFundPage funds={fundsData?.data || []} />
        </Wrapper>
      );
    }
  }

  if (pageDetails?.locked) return <PageLocked />;

  const childWithProps =
    children && React.isValidElement(children)
      ? React.cloneElement(children, {
        section: section || '',
        pageId,
        fundId,
        fundName: fundName || '',
        positionDate: activeDate || undefined,
        availableDates: availableDates || undefined,
      } as FundInfoComponentProps)
      : null;

  if (pageId === activePage) {
    return (
      <Wrapper fundsData={fundsData} fillAvailableSpace={fillAvailableSpace}>
        {isWaitingForPositionDate ? positionDateLoading : childWithProps}
      </Wrapper>
    );
  }

  return null;
};

const PageLocked: FunctionComponent = () => {
  return (
    <div className="flex h-[calc(100vh-10.00rem)] w-full flex-col items-center justify-center">
      <h2 className="text-3xl font-normal text-mainblue">
        Page Currently Unavailable
      </h2>
    </div>
  );
};

const useStyles = makeStyles<RaptorTheme>((theme) => ({
  lowerText: {
    marginBottom: '0.62rem',
  },
  allText: {
    color: 'white',
  },
  status: { fontSize: '1.12rem', marginBottom: 0 },
  alpha: {
    fontSize: '1.50rem',
    marginBottom: '0.62rem',
  },
  divider: {
    backgroundColor: theme.palette.grey[300],
    width: '100%',
    height: 1,
  },
}));

function SimpleSelectAFundPage({ funds }: { funds: any[] }) {
  const location = useLocation();
  const searchString = useSelector(currentFundSearchStringSelector);
  const clientName = useSelector(clientNameSelector) || 'mersenne';
  const addedClasses = useStyles();

  const selectedSortingKey = useTitleBarSelect('statusSort');
  if (!funds || funds.length === 0) {
    return <NoDataMessage message="No Funds Authorised" />;
  }

  const filteredFunds = [...funds]
    .filter((fund) =>
      (fund.fund_name || '').toLowerCase().includes(searchString.toLowerCase()),
    )
    .sort((a, b) => sortFundNames(a.fund_name || '', b.fund_name || ''));

  const Title = ({ title }: { title: string }) => (
    <Typography className={addedClasses.allText} variant="subtitle2">
      {title}
    </Typography>
  );
  const LowerText = ({ text, ...rest }: { text: string; status?: boolean }) => (
    <Typography
      className={clsx(
        addedClasses.lowerText,
        addedClasses.allText,
        rest.status && addedClasses.status,
      )}
      variant="h3"
    >
      {text}
    </Typography>
  );
  return (
    <>
      {selectedSortingKey === 'name'
        ? 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
          .split('')
          .filter(
            (letter) =>
              !searchString.toLowerCase() ||
              searchString[0].toLowerCase() === letter.toLowerCase(),
          )
          .map((letter) => (
            <>
              <GridItem xs={12}>
                <div>Select A Fund Page</div>
                <Typography className={addedClasses.alpha} variant="h3">
                  {letter}
                </Typography>
                <div className={addedClasses.divider} />
              </GridItem>
              {funds
                .filter((el: any) =>
                  el.name.toLowerCase().includes(searchString.toLowerCase()),
                )
                .filter(
                  (el: any) =>
                    el.name[0].toLowerCase() === letter.toLowerCase(),
                )
                .map((datum: any) => {
                  return (
                    <OverviewCard
                      clickHandler={() => null}
                      toVal={location.pathname + '?fundId=' + datum[0]}
                      key={datum.id + '_overview_card_key'}
                      backgroundColor={mainColors.mainBlue}
                    >
                      <div>
                        <Title title="name" />
                        <LowerText text={datum.name} />
                      </div>
                    </OverviewCard>
                  );
                })}
            </>
          ))
        : filteredFunds
          .sort((a, b) => sortFundNames(a.fund_name || '', b.fund_name || ''))
          .map((fund, index) => (
            <OverviewCard
              toVal={location.pathname + '?fundId=' + fund.fund_name}
              clickHandler={() => null}
              key={fund.fund_id + '_overview_card_key'}
              backgroundColor={mainColors.mainBlue}
            >
              <div>
                <Title title="ID" />
                <LowerText
                  text={
                    clientName.includes('mersenne')
                      ? `Fund ${index + 1}`
                      : fund.fund_name
                  }
                />
                <Title title="name" />
                <LowerText text={fund.fund_name_full} />
              </div>
            </OverviewCard>
          ))}
    </>
  );
}
